import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAppContext } from "../AppContext";
import { MyDomain } from "../App";
import "react-phone-input-2/lib/style.css";
import PhoneInput from "react-phone-input-2";
import { useTranslation } from "react-i18next";

const PRIMARY = "#e11d48";
const PRIMARY_LIGHT = "rgba(225,29,72,0.08)";
const PRIMARY_SHADOW = "rgba(225,29,72,0.28)";

function isErrorMsg(msg) {
  return (
    msg.includes("Error") ||
    msg.includes("Invalid") ||
    msg.includes("Failed") ||
    msg.includes("error") ||
    msg.includes("invalid") ||
    msg.includes("failed")
  );
}

const inputStyle = {
  width: "100%",
  height: 48,
  fontSize: 16,
  padding: "0 14px",
  border: "1.5px solid #e5e7eb",
  borderRadius: 10,
  outline: "none",
  transition: "border-color 0.2s",
  boxSizing: "border-box",
  color: "#1a1a1a",
  background: "#fff",
};

const primaryBtn = (disabled) => ({
  width: "100%",
  height: 50,
  fontSize: 15,
  fontWeight: 700,
  border: "none",
  borderRadius: 10,
  background: disabled ? "#d1d5db" : PRIMARY,
  color: "#fff",
  cursor: disabled ? "not-allowed" : "pointer",
  boxShadow: disabled ? "none" : `0 4px 14px ${PRIMARY_SHADOW}`,
  transition: "all 0.2s",
  letterSpacing: 0.3,
});

const ghostBtn = {
  flex: 1,
  height: 44,
  fontSize: 13,
  fontWeight: 600,
  border: "1.5px solid #e5e7eb",
  borderRadius: 10,
  background: "#fff",
  color: "#555",
  cursor: "pointer",
  transition: "border-color 0.2s, color 0.2s",
};

function StepDot({ step, authStep }) {
  const done = authStep > step;
  const active = authStep === step;
  return (
    <div
      style={{
        width: 32,
        height: 32,
        borderRadius: "50%",
        background: done || active ? PRIMARY : "#f3f4f6",
        color: done || active ? "#fff" : "#9ca3af",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 700,
        fontSize: 13,
        transition: "background 0.3s",
        flexShrink: 0,
      }}
    >
      {done ? "✓" : step}
    </div>
  );
}

function StatusBanner({ message }) {
  if (!message) return null;
  const error = isErrorMsg(message);
  return (
    <div
      style={{
        padding: "10px 14px",
        borderRadius: 8,
        background: error ? "#fee2e2" : "#d1fae5",
        color: error ? "#991b1b" : "#065f46",
        fontSize: 13,
        textAlign: "center",
        lineHeight: 1.5,
      }}
    >
      {message}
    </div>
  );
}

function LoginPage({ onLoginSuccess }) {
  const { t } = useTranslation();
  const { setUser } = useAppContext();
  const [authStep, setAuthStep] = useState(1);
  const [otp, setOtp] = useState("");
  const [message, setMessage] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryCode, setCountryCode] = useState("il");
  const [callingCode, setCallingCode] = useState("972");
  const [userName, setUserName] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem("user");
    if (saved) {
      const userData = JSON.parse(saved);
      setUser(userData);
      if (onLoginSuccess) onLoginSuccess();
    }
  }, []);

  const sendOTP = async () => {
    if (!phoneNumber || phoneNumber.length < 7) {
      setMessage(t("PleaseEnterValidPhone"));
      return;
    }
    setMessage("");
    setLoading(true);
    try {
      let cleaned = phoneNumber;
      if (cleaned[0] === "0") cleaned = cleaned.slice(1);
      const full = callingCode + cleaned;
      const isIsrael = callingCode === "972";
      const isPalestine = callingCode === "970";
      const endpoint = isIsrael
        ? `${MyDomain}SMS4FreeOtp/send`
        : isPalestine
          ? `${MyDomain}TweetSmsOtp/send`
          : `${MyDomain}Otp/send`;
      const response = await axios.post(endpoint, {
        ...(isPalestine ? { phoneNumber: "0" + cleaned } : { Phone: full }),
      });
      const ok =
        isIsrael || isPalestine
          ? response.status >= 200 && response.status < 300
          : response.data.success;
      if (ok) {
        setMessage(t("OTPSentTo") + " " + full);
        setAuthStep(2);
      } else {
        throw new Error(response.data.message || "Failed to send OTP");
      }
    } catch (error) {
      setMessage(error.response?.data?.message || error.message || t("ErrorMessage"));
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async () => {
    if (!otp || otp.length < 6) {
      setMessage(t("PleaseEnterValidOTP"));
      return;
    }
    setLoading(true);
    try {
      let cleaned = phoneNumber;
      if (cleaned[0] === "0") cleaned = cleaned.slice(1);
      const full = callingCode + cleaned;
      const isIsrael = callingCode === "972";
      const isPalestine = callingCode === "970";
      const endpoint = isIsrael
        ? `${MyDomain}SMS4FreeOtp/verify`
        : isPalestine
          ? `${MyDomain}TweetSmsOtp/verify`
          : `${MyDomain}Otp/verify`;
      const response = await axios.post(endpoint, {
        ...(isPalestine
          ? { phoneNumber: "0" + cleaned, code: otp }
          : { Phone: full, Otp: otp }),
      });
      const ok =
        isIsrael || isPalestine
          ? response.status >= 200 && response.status < 300
          : response.data.verified;
      if (ok) {
        setMessage(t("PhoneVerifiedSuccessfully"));
        setAuthStep(3);
      } else {
        throw new Error("Invalid verification code");
      }
    } catch (err) {
      setMessage(err.response?.data?.message || t("InvalidOTP"));
    } finally {
      setLoading(false);
    }
  };

  const resendOTP = async () => {
    setOtp("");
    setMessage("");
    await sendOTP();
  };

  const saveUserToBackend = async () => {
    if (!userName.trim()) {
      setMessage(t("PleaseEnterYourName"));
      return;
    }
    setLoading(true);
    try {
      let cleaned = phoneNumber;
      if (cleaned[0] === "0") cleaned = cleaned.slice(1);
      const data = {
        CountryNumber: "+" + callingCode,
        PhoneNumber: cleaned.startsWith("0") ? cleaned : "0" + cleaned,
        Name: userName,
      };
      const response = await axios.post(
        `${MyDomain}Users2/UserLoginWithPhoneNumber`,
        data,
        { headers: { "Content-Type": "application/json" } },
      );
      localStorage.setItem("user", JSON.stringify(response.data));
      setUser(response.data);
      setMessage(t("LoginSuccessful"));
      if (onLoginSuccess) setTimeout(() => onLoginSuccess(), 500);
    } catch (err) {
      setMessage(t("FailedToSaveUser") + " " + (err.response?.data || err.message));
    } finally {
      setLoading(false);
    }
  };

  const goBackToPhoneEntry = () => {
    setAuthStep(1);
    setOtp("");
    setMessage("");
  };

  const steps = [
    { n: 1, label: t("Phone") },
    { n: 2, label: t("Verify") },
    { n: 3, label: t("Name") },
  ];

  return (
    <div className="login-page-wrap">
      <div className="login-card">

        {/* Step progress */}
        <div className="login-steps">
          {steps.map(({ n, label }, i) => (
            <React.Fragment key={n}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
                <StepDot step={n} authStep={authStep} />
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: authStep === n ? 700 : 500,
                    color: authStep >= n ? PRIMARY : "#9ca3af",
                    transition: "color 0.3s",
                  }}
                >
                  {label}
                </span>
              </div>
              {i < 2 && (
                <div
                  style={{
                    flex: 1,
                    height: 2,
                    background: authStep > n ? PRIMARY : "#e5e7eb",
                    marginBottom: 18,
                    borderRadius: 2,
                    transition: "background 0.3s",
                  }}
                />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* ── STEP 1: Phone ── */}
        {authStep === 1 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            <div style={{ textAlign: "center" }}>
              <h2 style={{ margin: "0 0 6px", fontSize: 20, fontWeight: 700, color: "#1a1a1a" }}>
                {t("Welcome")}
              </h2>
              <p style={{ margin: 0, fontSize: 14, color: "#6b7280" }}>
                {t("EnterPhoneToGetStarted")}
              </p>
            </div>

            <div>
              <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 8 }}>
                {t("PhoneNumber")}
              </label>

              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                {/* Country flag picker */}
                <div style={{ flexShrink: 0, position: "relative" }}>
                  <PhoneInput
                    country={countryCode}
                    value=""
                    onChange={(value, country) => {
                      setCountryCode(country.countryCode);
                      setCallingCode(country.dialCode);
                    }}
                    enableSearch
                    inputStyle={{ display: "none" }}
                    buttonStyle={{
                      width: 68,
                      height: 48,
                      borderRadius: 10,
                      border: "1.5px solid #e5e7eb",
                      background: "#fff",
                      position: "static",
                    }}
                    containerStyle={{ width: 68, height: 48 }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      bottom: -20,
                      left: 0,
                      right: 0,
                      textAlign: "center",
                      fontSize: 11,
                      fontWeight: 700,
                      color: PRIMARY,
                    }}
                  >
                    +{callingCode}
                  </div>
                </div>

                <input
                  type="tel"
                  placeholder="50-123-4567"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value.replace(/[^0-9]/g, ""))}
                  onKeyPress={(e) => e.key === "Enter" && sendOTP()}
                  style={{ ...inputStyle, flex: 1 }}
                  onFocus={(e) => (e.target.style.borderColor = PRIMARY)}
                  onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
                />
              </div>

              <div
                style={{
                  marginTop: 28,
                  padding: "9px 12px",
                  background: "#f9fafb",
                  borderRadius: 8,
                  fontSize: 12,
                  color: "#6b7280",
                }}
              >
                {t("FullNumber")}{" "}
                <strong style={{ color: "#1a1a1a" }}>+{callingCode + phoneNumber}</strong>
              </div>
            </div>

            <StatusBanner message={message} />

            <button
              onClick={sendOTP}
              disabled={loading || !phoneNumber}
              style={primaryBtn(loading || !phoneNumber)}
            >
              {loading ? t("Sending") : t("SendVerificationCode")}
            </button>
          </div>
        )}

        {/* ── STEP 2: Verify OTP ── */}
        {authStep === 2 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            <div style={{ textAlign: "center" }}>
              <h2 style={{ margin: "0 0 6px", fontSize: 20, fontWeight: 700, color: "#1a1a1a" }}>
                {t("VerifyYourNumber")}
              </h2>
              <p style={{ margin: 0, fontSize: 14, color: "#6b7280" }}>
                {t("WeSentCodeTo")}{" "}
                <strong style={{ color: "#1a1a1a" }}>+{callingCode + phoneNumber}</strong>
              </p>
            </div>

            <div>
              <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 8 }}>
                {t("VerificationCode")}
              </label>
              <input
                type="text"
                inputMode="numeric"
                pattern="\d*"
                placeholder="• • • • • •"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
                onKeyPress={(e) => e.key === "Enter" && verifyOTP()}
                maxLength={6}
                style={{
                  ...inputStyle,
                  height: 58,
                  fontSize: 26,
                  textAlign: "center",
                  letterSpacing: 10,
                  fontWeight: 700,
                }}
                onFocus={(e) => (e.target.style.borderColor = PRIMARY)}
                onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
              />
            </div>

            <StatusBanner message={message} />

            <button
              onClick={verifyOTP}
              disabled={loading || otp.length < 6}
              style={primaryBtn(loading || otp.length < 6)}
            >
              {loading ? t("Verifying") : t("VerifyCode")}
            </button>

            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={resendOTP} disabled={loading} style={ghostBtn}>
                {loading ? t("Sending") : t("ResendCode") || "Resend Code"}
              </button>
              <button onClick={goBackToPhoneEntry} style={ghostBtn}>
                {t("ChangePhoneNumber")}
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 3: Name ── */}
        {authStep === 3 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            <div style={{ textAlign: "center" }}>
              <h2 style={{ margin: "0 0 6px", fontSize: 20, fontWeight: 700, color: "#1a1a1a" }}>
                {t("AlmostThere")}
              </h2>
              <p style={{ margin: 0, fontSize: 14, color: "#6b7280" }}>
                {t("EnterNameToComplete")}
              </p>
            </div>

            <div>
              <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 8 }}>
                {t("YourName")}
              </label>
              <input
                type="text"
                placeholder={t("EnterFullName")}
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && saveUserToBackend()}
                style={inputStyle}
                onFocus={(e) => (e.target.style.borderColor = PRIMARY)}
                onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
              />
            </div>

            <StatusBanner message={message} />

            <button
              onClick={saveUserToBackend}
              disabled={loading || !userName.trim()}
              style={primaryBtn(loading || !userName.trim())}
            >
              {loading ? t("Completing") : t("CompleteRegistration")}
            </button>
          </div>
        )}
      </div>

      <style>{`
        .login-page-wrap {
          min-height: 72vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 32px 16px 48px;
          background: #f9fafb;
        }
        .login-card {
          width: 100%;
          max-width: 420px;
          background: #fff;
          border-radius: 20px;
          box-shadow: 0 4px 28px rgba(0,0,0,0.08);
          padding: 36px 32px;
        }
        .login-steps {
          display: flex;
          align-items: center;
          gap: 0;
          margin-bottom: 32px;
        }
        @media (max-width: 480px) {
          .login-card {
            padding: 28px 20px;
            border-radius: 16px;
          }
          .login-page-wrap {
            padding: 20px 12px 40px;
            align-items: flex-start;
          }
        }
      `}</style>
    </div>
  );
}

export default LoginPage;
